import React, { useState } from 'react';
import {
  FaBullseye, FaChartLine, FaCheckCircle, FaChevronDown,
  FaGraduationCap, FaHome, FaShieldAlt, FaUmbrellaBeach,
  FaCar, FaCoins
} from 'react-icons/fa';
import { Helmet } from 'react-helmet-async';
const GoalBasedInvestments = () => {
  // State for Accordion FAQ toggling
  const [openFaq, setOpenFaq] = useState(null);

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const faqs = [
    {
      q: "What is Goal-Based Investing?",
      a: "Goal-Based Investing is an investment strategy where you segment your financial objectives into specific life milestones (e.g., buying a home, children's education, retirement) with defined timelines and target amounts. Investments are then tailored specifically to match the risk and duration of each individual goal rather than chasing generic market returns."
    },
    {
      q: "Why is traditional 'one-size-fits-all' investing ineffective?",
      a: "Traditional investing often focuses solely on beating a benchmark without considering what the money is actually meant to achieve. Without specific targets or timelines, investors frequently panic during market crashes, pull out funds prematurely, or fail to accumulate enough capital for major life expenses."
    },
    {
      q: "How do you account for inflation in goal-based planning?",
      a: "Inflation significantly increases the future cost of milestones like higher education or medical care. We factor in historical and projected inflation rates (e.g., 6% to 8% for education) to calculate the exact future value required, ensuring your investments don't fall short."
    },
    {
      q: "Can I adjust my goals if my life circumstances change?",
      a: "Absolutely. Goal-based plans are flexible roadmaps. If your income increases, your family grows, or your timeline shifts, our advisors recalibrate your monthly savings (SIPs) and asset allocation to keep you firmly on track."
    }
  ];

  return (
    <main className="goal-based-investments-page">
      <Helmet>
        {/* Basic SEO */}
        <title>Goal Based Investment Services in Hyderabad | Goal Based Financial Planning | Lotoswealth</title>

        <meta
          name="description"
          content="Lotoswealth offers Goal Based Investment Services in Hyderabad, helping individuals and families achieve life goals through personalized investment strategies for retirement, children's education, wealth creation, home buying, marriage planning, and long-term financial security."
        />

        <meta
          name="keywords"
          content="Goal Based Investments Hyderabad, Goal Based Investment Services Hyderabad, Goal Based Financial Planning Hyderabad, Goal Based Investing Hyderabad, Financial Planning Hyderabad, Investment Planning Hyderabad, Wealth Management Hyderabad, Retirement Planning Hyderabad, Child Education Planning Hyderabad, Marriage Planning Hyderabad, Home Purchase Planning Hyderabad, Wealth Creation Hyderabad, SIP Investment Hyderabad, Mutual Funds Hyderabad, Portfolio Management Hyderabad, Asset Management Hyderabad, Financial Advisor Hyderabad, Investment Advisor Hyderabad, Long Term Investment Hyderabad, Lotoswealth"
        />

        <meta name="robots" content="index, follow" />
        <meta name="author" content="Lotoswealth" />
        <meta name="language" content="English" />

        <link
          rel="canonical"
          href="https://www.lotoswealth.com/goal-based-investments"
        />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Lotoswealth" />
        <meta
          property="og:url"
          content="https://www.lotoswealth.com/goal-based-investments"
        />
        <meta
          property="og:title"
          content="Goal Based Investment Services in Hyderabad | Lotoswealth"
        />
        <meta
          property="og:description"
          content="Achieve your financial goals with Goal Based Investment Services from Lotoswealth. Personalized investment strategies for retirement, education, wealth creation, home buying, and long-term financial success."
        />
        <meta
          property="og:image"
          content="https://www.lotoswealth.com/images/lotoswealth-logo.png"
        />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Goal Based Investment Services in Hyderabad | Lotoswealth"
        />
        <meta
          name="twitter:description"
          content="Plan your future with Goal Based Investment Services in Hyderabad. Customized investment solutions for retirement, education, home purchase, wealth creation, and financial independence."
        />
        <meta
          name="twitter:image"
          content="https://www.lotoswealth.com/images/lotoswealth-logo.png"
        />
      </Helmet>
      {/* =========================================
          1. HERO SECTION (With Ticker & Slow Zoom)
      ========================================= */}
      <div className="hero-wrapper position-relative" style={{ height: '100vh', minHeight: '800px', overflow: 'hidden' }}>

        <div className="position-absolute top-0 start-0 w-100 h-100 z-0 animate-slow-zoom">
          <img
            src="/images/goal-based-investments.jpg"
            alt="Goal-Based Investments Advisory"
            className="w-100 h-100 object-fit-cover"
          />
        </div>

        {/* Slanted Panels Overlay */}
        <div className="panels-container position-absolute top-0 start-0 w-100 h-100 z-1 d-flex">
          <div className="slanted-panel panel-1"></div>
          <div className="slanted-panel panel-2"></div>
          <div className="slanted-panel panel-3"></div>
          <div className="slanted-panel panel-4"></div>
        </div>

        {/* --- Floating Main Header Text (Perfectly Centered) --- */}
        <div className="container hero-main-text text-center position-absolute top-50 start-50 translate-middle z-3 w-100 px-4">
          <h1 className="fw-bold display-4 animate-slide-up delay-1 mb-3 text-white" style={{ textShadow: '2px 2px 4px rgba(0,0,0,0.5)' }}>
            Goal-Based Investing
          </h1>
          <h2 className="animate-slide-up delay-2 fw-bold text-white mb-4" style={{ textShadow: '1px 1px 3px rgba(0,0,0,0.5)' }}>
            Turn Your Dreams Into Structured Financial Milestones
          </h2>
          <p className="hero-description animate-slide-up delay-3 text-white mx-auto" style={{ maxWidth: '950px', fontSize: '1.1rem', lineHeight: '1.7', textShadow: '1px 1px 3px rgba(0,0,0,0.5)' }}>
            Welcome to Lotoswealth. Investing without a clear destination is like sailing without a compass. Our Goal-Based Investing approach eliminates guesswork by tying every single rupee you invest directly to a meaningful life milestone with a specific timeline and target.
          </p>
          <p className="hero-description mt-3 animate-slide-up delay-4 text-white mx-auto" style={{ maxWidth: '950px', fontSize: '1.1rem', lineHeight: '1.7', textShadow: '1px 1px 3px rgba(0,0,0,0.5)' }}>
            Whether you are saving for your child's overseas university education, putting down a payment for your dream home, or securing an abundant retirement fund, we design disciplined asset allocations that protect your short-term needs while compounding your long-term growth.
          </p>
          <p className="hero-description mt-3 animate-slide-up delay-5 text-white mx-auto" style={{ maxWidth: '950px', fontSize: '1.1rem', lineHeight: '1.7', textShadow: '1px 1px 3px rgba(0,0,0,0.5)' }}>
            Stop chasing random market returns. Build a purpose-driven portfolio that transforms your life ambitions into predictable financial reality.
          </p>
        </div>

        {/* --- Scrolling Stock Ticker --- */}
        <div className="stock-ticker position-absolute bottom-0 w-100 z-3 bg-dark text-white border-top border-bottom border-secondary py-2" style={{ opacity: 0.9 }}>
          <div className="ticker-track">
            {/* First Set */}
            <div className="ticker-item text-white border-end border-secondary"><strong>APOLLOHOSP</strong> 8,894.00 <span className="text-danger">-63.00 (-0.70%)</span></div>
            <div className="ticker-item text-white border-end border-secondary"><strong>ASIANPAINT</strong> 2,768.60 <span className="text-success">21.30 (0.78%)</span></div>
            <div className="ticker-item text-white border-end border-secondary"><strong>AXISBANK</strong> 1,250.00 <span className="text-success">20.50 (1.67%)</span></div>
            <div className="ticker-item text-white border-end border-secondary"><strong>BAJAJ-AUTO</strong> 11,519.00 <span className="text-danger">-2.00 (-0.02%)</span></div>
            <div className="ticker-item text-white border-end border-secondary"><strong>BAJAJFINSV</strong> 2,085.60 <span className="text-success">56.50 (2.78%)</span></div>

            {/* Duplicated Set for Infinite Scrolling */}
            <div className="ticker-item text-white border-end border-secondary"><strong>APOLLOHOSP</strong> 8,894.00 <span className="text-danger">-63.00 (-0.70%)</span></div>
            <div className="ticker-item text-white border-end border-secondary"><strong>ASIANPAINT</strong> 2,768.60 <span className="text-success">21.30 (0.78%)</span></div>
            <div className="ticker-item text-white border-end border-secondary"><strong>AXISBANK</strong> 1,250.00 <span className="text-success">20.50 (1.67%)</span></div>
            <div className="ticker-item text-white border-end border-secondary"><strong>BAJAJ-AUTO</strong> 11,519.00 <span className="text-danger">-2.00 (-0.02%)</span></div>
            <div className="ticker-item text-white border-end border-secondary"><strong>BAJAJFINSV</strong> 2,085.60 <span className="text-success">56.50 (2.78%)</span></div>
          </div>
        </div>
      </div>

      {/* =========================================
          2. WHY GOAL-BASED INVESTING MATTERS (Soft Teal Theme)
      ========================================= */}
      <section className="py-5" style={{ backgroundColor: '#f0f9f6' }}>
        <div className="container py-4">
          <div className="row align-items-center g-5">
            <div className="col-lg-6 animate-slide-up delay-1">
              <h2 className="fw-bold mb-4 text-dark">
                Why Goal-Based Investing Works Better
              </h2>
              <p className="text-muted line-height-lg">
                Traditional investing often leaves individuals treating their savings as a single, generic pool of money. This makes it easy to dip into funds meant for long-term milestones when short-term temptations arise.
              </p>
              <p className="text-muted line-height-lg">
                By segregating investments into distinct buckets with clear tags (e.g., "Child Education - 2035"), you establish behavioral discipline. Furthermore, it dictates the precise asset mix required: short-term goals stay protected in low-risk debt, while long-term goals harness equity compounding.
              </p>
            </div>
            <div className="col-lg-6 animate-slide-up delay-2">
              <div className="p-4 bg-white rounded-4 shadow-sm border-start border-teal border-5">
                <h4 className="fw-bold text-teal mb-3">Core Advantages</h4>
                <ul className="list-unstyled text-muted mb-0">
                  <li className="mb-2"><FaCheckCircle className="text-teal me-2" /> <strong>Behavioral Discipline:</strong> Reduces panic-selling during market slumps.</li>
                  <li className="mb-2"><FaCheckCircle className="text-teal me-2" /> <strong>Precise Asset Allocation:</strong> Matches risk profile with goal timeframes.</li>
                  <li className="mb-2"><FaCheckCircle className="text-teal me-2" /> <strong>Inflation Guard:</strong> Calculates exact future values accounting for cost escalations.</li>
                  <li className="mb-2"><FaCheckCircle className="text-teal me-2" /> <strong>Absolute Clarity:</strong> Know precisely whether you are ahead or behind schedule.</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* =========================================
          3. OUR CORE GOAL CATEGORIES (Crisp White)
      ========================================= */}
      <section className="py-5 bg-white" style={{ backgroundImage: "url('/images/lotoswealth-bg.jpg')", backgroundSize: 'cover', backgroundPosition: 'center' }}>

        <div className="container py-4">
          <div className="text-center mb-5 animate-slide-up delay-1">
            <h2 className="fw-bold text-white">🎯 Key Milestone Categories We Plan For</h2>
            <p className="text-white">Structuring investments around the most important moments of your life.</p>
          </div>

          <div className="row g-4">

            <div className="col-lg-4 col-md-6 animate-slide-up delay-1">
              <div className="p-4 bg-light rounded-4 h-100 hover-lift-card border-bottom border-teal border-4">
                <FaGraduationCap size={36} className="text-teal mb-3" />
                <h5 className="fw-bold mb-2">Children's Education & Marriage</h5>
                <p className="text-muted small">Combat steep education inflation by starting targeted equity and hybrid SIPs early, ensuring your children's global university dreams are fully funded without debt.</p>
              </div>
            </div>

            <div className="col-lg-4 col-md-6 animate-slide-up delay-2">
              <div className="p-4 bg-light rounded-4 h-100 hover-lift-card border-bottom border-teal border-4">
                <FaHome size={36} className="text-teal mb-3" />
                <h5 className="fw-bold mb-2">Dream Home Acquisition</h5>
                <p className="text-muted small">Systematically build your down payment corpus or pre-pay high-interest home loans using balanced asset allocation strategies designed for property timelines.</p>
              </div>
            </div>

            <div className="col-lg-4 col-md-6 animate-slide-up delay-3">
              <div className="p-4 bg-light rounded-4 h-100 hover-lift-card border-bottom border-teal border-4">
                <FaUmbrellaBeach size={36} className="text-teal mb-3" />
                <h5 className="fw-bold mb-2">Retirement Corpus Building</h5>
                <p className="text-muted small">Calculate your post-retirement lifestyle needs and leverage long-term equity compounding to accumulate a robust corpus that sustains you for decades.</p>
              </div>
            </div>

            <div className="col-lg-4 col-md-6 animate-slide-up delay-4">
              <div className="p-4 bg-light rounded-4 h-100 hover-lift-card border-bottom border-teal border-4">
                <FaShieldAlt size={36} className="text-teal mb-3" />
                <h5 className="fw-bold mb-2">Emergency Contingency Fund</h5>
                <p className="text-muted small">Keep 6 to 12 months of essential living expenses parked in high-liquidity, low-risk instruments to shield your long-term goals from unexpected life shocks.</p>
              </div>
            </div>

            <div className="col-lg-4 col-md-6 animate-slide-up delay-5">
              <div className="p-4 bg-light rounded-4 h-100 hover-lift-card border-bottom border-teal border-4">
                <FaCar size={36} className="text-teal mb-3" />
                <h5 className="fw-bold mb-2">Lifestyle & Short-Term Goals</h5>
                <p className="text-muted small">Plan for luxury assets, international vacations, or vehicle purchases using short-term debt and arbitrage funds that protect principal while earning steady yields.</p>
              </div>
            </div>

            <div className="col-lg-4 col-md-6 animate-slide-up delay-6">
              <div className="p-4 bg-light rounded-4 h-100 hover-lift-card border-bottom border-teal border-4">
                <FaCoins size={36} className="text-teal mb-3" />
                <h5 className="fw-bold mb-2">Legacy & Philanthropy</h5>
                <p className="text-muted small">Structure long-term surplus capital into philanthropic trusts, family endowments, or multi-generational wealth transfers to leave a lasting impact.</p>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* =========================================
          4. OUR 5-STEP GOAL PROCESS (Dark Slate)
      ========================================= */}
      <section className="py-5 text-white" style={{ backgroundColor: '#0a192f' }}>
        <div className="container py-4">
          <div className="text-center mb-5 animate-slide-up delay-1">
            <h2 className="fw-bold text-white">🔄 Our Goal-Based Methodology</h2>
            <p className="text-light opacity-75">A systematic path from ambition to achievement.</p>
          </div>

          <div className="row g-4">

            <div className="col-md-6 col-lg-4 animate-slide-up delay-1">
              <div className="p-4 bg-white text-dark rounded-4 shadow-sm h-100 border-top border-4 border-teal">
                <h5 className="fw-bold text-teal mb-2">Step 1 – Goal Identification</h5>
                <p className="small text-muted mb-0">We sit down with you to list, prioritize, and categorize all personal milestones into short-term, medium-term, and long-term buckets.</p>
              </div>
            </div>

            <div className="col-md-6 col-lg-4 animate-slide-up delay-2">
              <div className="p-4 bg-white text-dark rounded-4 shadow-sm h-100 border-top border-4 border-teal">
                <h5 className="fw-bold text-teal mb-2">Step 2 – Inflation Adjustment</h5>
                <p className="small text-muted mb-0">We calculate the exact future cost of each goal by applying sector-specific inflation rates (e.g., education vs. general lifestyle).</p>
              </div>
            </div>

            <div className="col-md-6 col-lg-4 animate-slide-up delay-3">
              <div className="p-4 bg-white text-dark rounded-4 shadow-sm h-100 border-top border-4 border-teal">
                <h5 className="fw-bold text-teal mb-2">Step 3 – Asset Mapping</h5>
                <p className="small text-muted mb-0">We match each goal with the optimal investment vehicle—equity mutual funds for long goals, debt/hybrid instruments for near-term goals.</p>
              </div>
            </div>

            <div className="col-md-6 col-lg-6 animate-slide-up delay-4">
              <div className="p-4 bg-white text-dark rounded-4 shadow-sm h-100 border-top border-4 border-teal">
                <h5 className="fw-bold text-teal mb-2">Step 4 – Systematic Execution</h5>
                <p className="small text-muted mb-0">Setting up automated SIPs, lump-sum deployments, and portfolio structures to fund each goal systematically without emotional interference.</p>
              </div>
            </div>

            <div className="col-md-12 col-lg-6 animate-slide-up delay-5">
              <div className="p-4 bg-white text-dark rounded-4 shadow-sm h-100 border-top border-4 border-teal">
                <h5 className="fw-bold text-teal mb-2">Step 5 – Milestone Tracking & Rebalancing</h5>
                <p className="small text-muted mb-0">Regularly reviewing goal progress, scaling down risk as a goal approaches, and ensuring you reach the finish line securely.</p>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* =========================================
          5. WHO WE SERVE & WHY CHOOSE US (Soft Blue-Gray)
      ========================================= */}
      <section className="py-5" style={{ backgroundColor: '#eef2f5' }}>
        <div className="container py-4">
          <div className="row g-5">

            {/* Target Audience */}
            <div className="col-lg-5 animate-slide-up delay-1">
              <h3 className="fw-bold mb-4 text-dark">👥 Who Benefits from Goal-Based Investing?</h3>
              <p className="text-muted small mb-4">This structured approach is ideal for anyone seeking clarity over chaos:</p>
              <div className="d-flex flex-wrap gap-2">
                {[
                  "Young Salaried Professionals", "Growing Families",
                  "Parents Planning Children's Futures", "Business Owners & Entrepreneurs",
                  "Pre-Retirees", "First-Time Home Buyers",
                  "Discipline-Seeking Investors", "Long-Term Wealth Creators"
                ].map((item, idx) => (
                  <span key={idx} className="badge bg-white text-dark border p-2 shadow-sm fs-6">
                    {item}
                  </span>
                ))}
              </div>
            </div>

            {/* Why Choose Lotoswealth */}
            <div className="col-lg-7 animate-slide-up delay-2">
              <div className="p-4 bg-white rounded-4 shadow-sm h-100">
                <h3 className="fw-bold mb-3 text-dark">🏆 Why Trust Lotoswealth?</h3>
                <p className="text-muted small mb-4">Achieving life milestones requires professional guidance and unwavering dedication. Clients choose us because we offer:</p>
                <div className="row g-3">
                  {[
                    "Customized Milestone Mapping", "Rigorous Inflation-Adjusted Math",
                    "Unbiased Fund & Asset Selection", "Systematic Portfolio Rebalancing",
                    "Dedicated Relationship Support", "Transparent & Honest Advisory",
                    "Digital Dashboard Tracking", "Client-First Long-Term Focus"
                  ].map((item, idx) => (
                    <div key={idx} className="col-md-6 d-flex align-items-center">
                      <FaCheckCircle className="text-success me-2 flex-shrink-0" />
                      <span className="small text-muted fw-bold">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* =========================================
          6. FREQUENTLY ASKED QUESTIONS (FAQ)
      ========================================= */}
      <section className="py-5" style={{ backgroundColor: '#f8f9fa' }}>
        <div className="container py-4" style={{ maxWidth: '850px' }}>
          <div className="text-center mb-5 animate-slide-up delay-1">
            <h2 className="fw-bold text-dark">❓ Frequently Asked Questions</h2>
            <p className="text-muted">Common queries regarding Goal-Based Investment Planning</p>
          </div>

          <div className="d-flex flex-column gap-3 animate-slide-up delay-2">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="bg-white rounded-3 shadow-sm border overflow-hidden"
              >
                <button
                  className="w-100 text-start p-3 border-0 bg-white d-flex justify-content-between align-items-center fw-bold text-dark"
                  onClick={() => toggleFaq(index)}
                  style={{ cursor: 'pointer' }}
                >
                  <span className="pe-3">{faq.q}</span>
                  <FaChevronDown
                    className={`text-teal transition-transform ${openFaq === index ? 'rotate-180' : ''}`}
                    style={{ transition: 'transform 0.3s ease', minWidth: '16px' }}
                  />
                </button>
                {openFaq === index && (
                  <div className="p-3 pt-0 text-muted small border-top bg-light lh-lg">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* =========================================
          7. FINAL CTA SECTION (Deep Navy)
      ========================================= */}
      <section className="py-5 text-white text-center" style={{ backgroundColor: '#0f1e2d' }}>
        <div className="container py-4">
          <h2 className="fw-bold mb-3 animate-slide-up delay-1">Ready to Map Out Your Financial Goals?</h2>
          <p className="text-light opacity-75 mb-4 animate-slide-up delay-2 mx-auto" style={{ maxWidth: '600px' }}>
            Stop guessing and start planning. Connect with our expert advisors today to build your personalized goal-based investment roadmap.
          </p>
          <button className="btn-custom-teal hover-lift px-5 py-3 fs-5 border-0 rounded text-white shadow animate-slide-up delay-3">
            Schedule a Goal-Based Consultation
          </button>
        </div>
      </section>

    </main>
  );
};

export default GoalBasedInvestments;